import React from 'react';
import { Shield, CheckCircle } from 'lucide-react';

function DoNotSell() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">iOS PreCheck</span>
            </div>
            <a href="/" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors">Back to Home</a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Do Not Sell My Personal Information</h1>
          </div>
          
          <p className="text-gray-600 mb-8">California Consumer Privacy Act (CCPA) Rights</p>

          <div className="prose prose-lg max-w-none">
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8 mb-8">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Good News!</h2>
                  <p className="text-gray-700 text-lg">
                    We do not sell your personal information to third parties. We never have, and we never will.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What This Means</h2>
            <p className="text-gray-700 mb-6">
              Under the California Consumer Privacy Act (CCPA), California residents have the right to opt out of the "sale" of their personal information. Since we don't sell your data, there's nothing to opt out of.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How We Use Your Data</h2>
            <p className="text-gray-700 mb-4">We only share your information with:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Stripe:</strong> To process your payment securely</li>
              <li><strong>Anthropic:</strong> To analyze your app metadata (not your binary code)</li>
              <li><strong>Infrastructure providers:</strong> To host and deliver our service (Railway, Cloudflare)</li>
            </ul>
            <p className="text-gray-700 mb-6">
              These are service providers who help us deliver our service to you. We don't receive any money from them for your data.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Your California Privacy Rights</h2>
            <p className="text-gray-700 mb-4">As a California resident, you have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Know what personal information we collect about you</li>
              <li>Know whether we sell or disclose your personal information</li>
              <li>Say no to the sale of your personal information</li>
              <li>Access your personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Not be discriminated against for exercising these rights</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Exercise Your Rights</h2>
            <p className="text-gray-700 mb-4">
              To request access to or deletion of your personal information, contact us at:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <ul className="list-none text-gray-700 space-y-2">
                <li><strong>Email:</strong> support@iosprecheck.com</li>
                <li><strong>Subject Line:</strong> "CCPA Data Request"</li>
              </ul>
            </div>
            <p className="text-gray-700 mb-6">
              We'll verify your identity and respond within 45 days as required by California law.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What We Collect</h2>
            <p className="text-gray-700 mb-4">
              For transparency, here's what personal information we collect:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Payment information:</strong> Email, billing address (processed by Stripe)</li>
              <li><strong>Usage data:</strong> IP address, browser type, pages visited</li>
              <li><strong>App files:</strong> Your .ipa file (temporarily stored, deleted within 1 hour)</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Questions?</h2>
            <p className="text-gray-700 mb-6">
              Read our full <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> or contact us at support@iosprecheck.com.
            </p>

            <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> This page is specifically for California residents' CCPA rights. All users can review our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> for information about data collection and use.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DoNotSell;